<template>
  <div class="row">
    <div class="col-md-12 card">
      <div class="card-header">
        <h4 class="title">Fleet drivers performance</h4>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-12">
            <p>
              Use the filters below to analyse the performance of your drivers
              based on key metrics, during the selected period.
            </p>
            <div class="card bg-light">
              <div class="card-body">
                <form class="form-inline">
                  <label for="statisticsPeriod">Period</label> &nbsp;
                  <select
                    class="form-control mb-2 mr-sm-2"
                    id="statisticsPeriod"
                    v-model="statisticsPeriod"
                  >
                    <option
                      v-for="option in statisticsPeriodOptions"
                      v-bind:key="option.key"
                      :value="option.key"
                      >{{ option.label }}</option
                    >
                  </select>

                  <label for="statisticsMetric">Metric</label> &nbsp;
                  <select
                    class="form-control mb-2 mr-sm-2"
                    id="statisticsMetric"
                    v-model="statisticsMetric"
                  >
                    <optgroup label="Sales">
                      <option
                        v-for="option in statisticsMetricOptions"
                        v-bind:key="option.key"
                        :value="option.key"
                        >{{ option.label }}</option
                      >
                    </optgroup>
                  </select>

                  <label for="statisticsSortDirection">Sort</label> &nbsp;
                  <select
                    class="form-control mb-2 mr-sm-2"
                    id="statisticsMetric"
                    v-model="statisticsSortDirection"
                  >
                    <option value="ascending">Lowest first</option>
                    <option value="descending">Highest first</option>
                  </select>
                </form>
              </div>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <active-filter-list
              :activeFilters="activeFilters"
              :tableName="tableName"
              @remove-filter="removeFilter"
              @clear-filters="clearFilters"
            ></active-filter-list>

            <v-server-table
              :name="tableName"
              url="/v1/submerchants"
              :columns="columns"
              :options="options"
              :ref="tableName"
              @filter="addToActiveFilters"
            >
              <template slot="id" slot-scope="props">
                <code>{{ props.row.id }}</code>
              </template>

              <template slot="reference" slot-scope="props">
                <code>{{ props.row.reference }}</code>
              </template>

              <template slot="isActive" slot-scope="props">
                <span v-if="props.row.isActive">Active</span>
                <span v-else-if="!props.row.isActive">Inactive</span>
              </template>

              <!-- valueSum templates -->
              <template
                v-for="period in statisticsPeriodOptions"
                :slot="
                  `statistics.sales.${period.key}.currentPeriod.summary.valueSum`
                "
                slot-scope="props"
              >
                <div
                  class="text-right"
                  v-if="
                    props.row.statistics.sales[period.key] &&
                      props.row.statistics.sales[period.key].currentPeriod
                        .summary.valueSum
                  "
                  v-bind:key="period.key"
                >
                  {{
                    props.row.statistics.sales[period.key].currentPeriod.summary
                      .valueSum | money("gbp")
                  }}
                </div>
                <span v-else v-bind:key="period.key">&hellip;</span>
              </template>

              <!-- valueAvg templates -->
              <template
                v-for="period in statisticsPeriodOptions"
                :slot="
                  `statistics.sales.${period.key}.currentPeriod.summary.valueAvg`
                "
                slot-scope="props"
              >
                <div
                  class="text-right"
                  v-if="
                    props.row.statistics.sales[period.key] &&
                      props.row.statistics.sales[period.key].currentPeriod
                        .summary.valueAvg
                  "
                  v-bind:key="period.key"
                >
                  {{
                    props.row.statistics.sales[period.key].currentPeriod.summary
                      .valueAvg | money("gbp")
                  }}
                </div>
                <span v-else v-bind:key="period.key">&hellip;</span>
              </template>

              <!-- valueMax templates -->
              <template
                v-for="period in statisticsPeriodOptions"
                :slot="
                  `statistics.sales.${period.key}.currentPeriod.summary.valueMax`
                "
                slot-scope="props"
              >
                <div
                  class="text-right"
                  v-if="
                    props.row.statistics.sales[period.key] &&
                      props.row.statistics.sales[period.key].currentPeriod
                        .summary.valueMax
                  "
                  v-bind:key="period.key"
                >
                  {{
                    props.row.statistics.sales[period.key].currentPeriod.summary
                      .valueMax | money("gbp")
                  }}
                </div>
                <span v-else v-bind:key="period.key">&hellip;</span>
              </template>

              <!-- valueMin templates -->
              <template
                v-for="period in statisticsPeriodOptions"
                :slot="
                  `statistics.sales.${period.key}.currentPeriod.summary.valueMin`
                "
                slot-scope="props"
              >
                <div
                  class="text-right"
                  v-if="
                    props.row.statistics.sales[period.key] &&
                      props.row.statistics.sales[period.key].currentPeriod
                        .summary.valueMin
                  "
                  v-bind:key="period.key"
                >
                  {{
                    props.row.statistics.sales[period.key].currentPeriod.summary
                      .valueMin | money("gbp")
                  }}
                </div>
                <span v-else v-bind:key="period.key">&hellip;</span>
              </template>

              <!-- count templates -->
              <template
                v-for="period in statisticsPeriodOptions"
                :slot="
                  `statistics.sales.${period.key}.currentPeriod.summary.count`
                "
                slot-scope="props"
              >
                <div
                  class="text-right"
                  v-if="
                    props.row.statistics.sales[period.key] &&
                      props.row.statistics.sales[period.key].currentPeriod
                        .summary.count
                  "
                  v-bind:key="period.key"
                >
                  {{
                    props.row.statistics.sales[period.key].currentPeriod.summary
                      .count
                  }}
                </div>
                <span v-else v-bind:key="period.key">&hellip;</span>
              </template>

              <template slot="links" slot-scope="props">
                <router-link
                  :to="`/drivers/${props.row.id}/performance`"
                  class="btn btn-default btn-sm"
                >
                  View performance &nbsp;
                  <i class="fa fa-chevron-right"></i>
                </router-link>
              </template>
            </v-server-table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import _ from "lodash";

import { requestAdapter } from "../../../../helpers/tableHelpers";
import ActiveFilterList from "src/components/UIComponents/ActiveFilterList.vue";

const columns = ["id", "name", "reference", "isActive", "links"];

export default {
  name: "SubmerchantsList",
  components: {
    ActiveFilterList
  },
  data() {
    return {
      statisticsPeriod: "thirtyDays",
      statisticsMetric: "valueSum",
      statisticsSortDirection: "descending",
      statisticsPeriodOptions: [
        { key: "allTime", label: "All time" },
        { key: "twelveMonths", label: "Past 12 months" },
        { key: "thirtyDays", label: "Past 30 days" },
        { key: "sevenDays", label: "Past 7 days" },
        { key: "twentyFourHours", label: "Past 24 hours" }
      ],
      statisticsMetricOptions: [
        { key: "valueSum", label: "Transaction volume" },
        { key: "count", label: "Transaction count" },
        { key: "valueAvg", label: "Average transaction value" },
        { key: "valueMin", label: "Lowest transaction value" },
        { key: "valueMax", label: "Highest transaction value" }
      ],
      columns,
      activeFilters: [],
      tableName: "submerchantsPerformanceList",
      options: {
        name: this.tableName,
        pagination: { chunk: 3 },
        requestAdapter,
        requestFunction: function(data) {
          const parent = this.$parent.$parent;
          data.expand = "statistics";
          data.fields = "id,name,reference,isActive,links";
          const statsField = `statistics.sales.${parent.statisticsPeriod}.currentPeriod.summary.${parent.statisticsMetric}`;
          data.fields += `,${statsField}`;

          // sort
          if (parent.statisticsSortDirection === "descending") {
            data.sort = `-${statsField}`;
          } else {
            data.sort = statsField;
          }

          return this.axios.get("v1/submerchants", { params: data }).catch(
            function(e) {
              this.dispatch("error", e);
            }.bind(this)
          );
        },
        columnsDropdown: false,
        headings: {
          id: "Driver ID",
          reference: "Reference",
          name: "Name",
          isActive: "Active"
        },
        sortable: [],
        filterable: _.initial(columns),
        listColumns: {
          isActive: [
            {
              id: "true",
              text: "Active"
            },
            {
              id: "false",
              text: "Inactive"
            }
          ]
        }
      }
    };
  },
  watch: {
    // whenever question changes, this function will run
    statisticsPeriod: function(newValue, oldValue) {
      this.setColumnsReloadTable();
    },
    statisticsMetric: function(newValue, oldValue) {
      this.setColumnsReloadTable();
    },
    statisticsSortDirection: function(newValue, oldValue) {
      this.setColumnsReloadTable();
    }
  },
  methods: {
    setColumnsReloadTable: function() {
      const columns = ["id", "name", "reference", "isActive"];

      const statsColumnName = `statistics.sales.${this.statisticsPeriod}.currentPeriod.summary.${this.statisticsMetric}`;
      columns.push(statsColumnName);
      columns.push("links"); // append links
      this.columns = columns;

      this.$refs[this.tableName].refresh();
    },
    clearFilters() {
      this.$refs[this.tableName].resetQuery();
      this.$refs[this.tableName].resetCustomFilters();
      this.activeFilters = [];
    },
    containsObject(obj, list) {
      for (const i in list) {
        if (obj.name === list[i].name) {
          return true;
        }
      }
      return false;
    },
    removeFilter(filter) {
      if (filter.name === "date") {
        filter.name = "capturedAt";
        Event.$emit(`vue-tables.${this.tableName}.filter::${filter.name}`, "");
      } else {
        const filterObj = {};
        filterObj[filter.name] = "";
        this.$refs[this.tableName].setFilter(filterObj);
      }
    },
    addToActiveFilters(filter) {
      let isDuplicate = this.containsObject(filter, this.activeFilters);

      if (filter.value !== "" && !isDuplicate) {
        this.activeFilters.push(filter);
      } else if (filter.value === "") {
        const index = this.activeFilters.indexOf(filter);
        this.activeFilters.splice(index, 1);
      }
    },
    loadActiveFilters() {
      const tableData = this.$refs[this.tableName].getRequestParams();
      const activeQueries = tableData["query"];
      for (let query in activeQueries) {
        if (activeQueries[query] !== "") {
          const queryObj = { name: query, value: activeQueries[query] };
          this.activeFilters.push(queryObj);
        }
      }
    }
  },
  beforeMount: function() {
    this.statisticsPeriodOptions.forEach(periodOption => {
      this.statisticsMetricOptions.forEach(metricOption => {
        const headingKey = `statistics.sales.${periodOption.key}.currentPeriod.summary.${metricOption.key}`;
        const headingLabel = `${periodOption.label} ${metricOption.label}`;
        this.options.headings[headingKey] = headingLabel;
      });
    });
  },
  mounted: function() {
    this.setColumnsReloadTable();
    this.loadActiveFilters();
  }
};
</script>
